// UI texts: landing page (routes / for signed-out visitors and /welcome)
export default {
  docTitle: 'AFTER DAY · Send your survivor. Come back to a story.',
  nav: {
    label: 'Main',
    home: 'AFTER DAY, back to top',
    how: 'How it works',
    fair: 'Fair play',
    shadow: 'Shadow',
    world: 'World',
    faq: 'FAQ',
    play: 'Play now',
    camp: 'Back to camp',
    signIn: 'Log in',
  },
  hero: {
    stamp: 'Public beta · open now',
    /** 13. 10. 2026 (owner, launch.md §3): the name, one sentence and Play now on the first screen; the longer pitch opens §01. */
    title: 'Send your survivor out, close the browser, come back to a story.',
    play: 'Play now',
    /** 29. 9. 2026: next to Start, for a survivor saved with an email or Google (13. 10. 2026: a smaller line under Play now). */
    returning: 'Played before?',
    logIn: 'Log in',
    continue: 'Continue your story',
    hint: 'Free · no download · first run in under a minute',
    phone: 'An expedition report on a phone: a Shadow ambush in Mercy General.',
  },
  how: {
    kicker: '§01 How it works',
    title: 'Three steps. Then walk away.',
    lead: "A survival RPG that plays while you're away, in a shared, ruined world. Give orders in a minute, let the day play out without you, then read the journal of everything that happened.",
    s1: { title: 'Choose a zone', body: 'From the quiet forest to the military base. More danger means better loot and fewer survivors walking back out.' },
    s2: { title: 'Give orders', body: 'Pick a stance, when to retreat and what to trade. Peaceful, Cautious, Aggressive or Shadow. Then close the tab.' },
    s3: { title: 'Read what happened', body: 'Come back to a field journal: who you met, what you found, what went wrong. Every run is a short story.' },
  },
  /** 13. 10. 2026 (owner, launch.md §3): "Power is never sold", in sight before the FAQ. */
  fair: {
    kicker: '§02 Fair play',
    title: 'Power is',
    titleHit: 'never sold.',
    stamp: 'No pay to win',
    body: 'Nothing you can buy makes your survivor stronger. What they carry, they found, crafted or took out there.',
    earned: { title: 'Earned out there', body: 'Zones, stances, skills and items are all unlocked by playing: runs, the Workshop, the horde. Never from a store.' },
    ads: { title: 'Ads are optional', body: 'Up to {n} a day, only when you want one: +{e} energy when you run low, or a full heal. That is all they do.' },
    season: { title: 'Everyone starts over', body: 'A season lasts {days} days. Then everyone starts again on level 1 with a fresh camp. Outfits, titles and the Collection book stay.' },
  },
  shadow: {
    kicker: '§03 The Shadow stance',
    title: 'Follow. Wait.',
    titleHit: 'Ambush.',
    body: 'Shadow survivors avoid a fair fight. They pick out someone carrying loot, stay out of sight and wait for the moment the odds turn. Then they steal and run, or attack for everything.',
    keyMoment: 'Key moment',
    follow: { time: '14:14', title: 'Follow', body: 'Spot a survivor with a full pack. Keep your distance through three areas.', art: 'A shadow trails a survivor carrying a blue pack.' },
    wait: { time: '14:19', title: 'Wait', body: 'They stop to loot. They get tired. You stay behind the wall.', art: 'The shadow waits behind a wall while the survivor rests.' },
    ambush: { time: '14:23', title: 'Ambush', body: 'Steal two Medkits and vanish. Or stay and fight for the whole pack.', art: 'The shadow strikes and grabs the pack.' },
  },
  world: {
    kicker: '§04 Shared world',
    title: 'One map. Every player is on it.',
    /** Package 4, part H: the world book's premise. */
    premise: 'Mercy Falls, two years after the Last Day. MEND was supposed to make everyone better. WARDEN still enforces the curfew.',
    body: 'Some of the survivors you meet are other players on their own runs. Rumors spread, zones heat up, and when a horde hits Downtown, everyone fights the same health bar.',
    online: 'survivors out there right now',
    /** Shown once there are enough survivors and runs today (LANDING_RULES). */
    totals: '{survivors} survivors so far · {runs} expeditions today',
    tracks: 'When you close the tab, your tracks stay out there. Others may find them, and if they beat your tracks, you get a Revenge token.',
    offline: 'Live count unavailable right now.',
    phone: 'The world map on a phone, with zones from Calder Woods to Fort Halden.',
  },
  stories: {
    kicker: '§05 Real stories',
    title: 'Stories from this week',
    /** No real card from the last 7 days yet: the examples. */
    kickerSamples: '§05 Every run is a story',
    titleSamples: 'What a run can turn into',
    example: 'Example',
    open: 'Open story: {headline}',
    brand: 'After Day',
  },
  more: {
    kicker: '§06 More to survive',
    title: 'Between the runs',
    camp: { title: 'Build your camp', body: 'Upgrade the workshop and storage. Craft weapons, armor and medkits from what you drag home.' },
    grow: { title: 'Grow a survivor', body: 'Five backgrounds, five attributes, one skill tree with six keystones. Soldier, Medic, Engineer, Hunter or Scavenger.' },
    // 11. 10. 2026 (owner, BACKLOG #33): crews.
    clan: { title: 'Join a crew', body: 'Five survivors at most, one flag: a crew chat, a weekly contract, crew levels with perks and chests, and the horde together.' },
    horde: { title: 'Survive the horde', body: 'Server-wide events where every player chips at one health bar. Top fighters earn Epic items.' },
    live: { title: 'Be there live', body: 'Online when your survivor meets someone? Choose yourself within 90 seconds: attack or escape by hand for a +10% bonus.' },
    rumors: { title: 'Chase rumors', body: 'A bunker in the northern forest? A trader with a dog? Some rumors are true. Be first to find out.' },
    /** The counts come from the item catalog, the rarities and the sets. */
    // 11. 10. 2026 (owner): while not every set has an outfit, the count says how many do (`outfitNote`); deploy8: the
    // Foundry's outfit came, so every set has one and the note is empty.
    gear: { title: 'Hunt for gear', body: '{items} items, {rarities} rarities, {sets} sets. Completing a set unlocks its outfit{outfitNote}; Legendary outfits are animated.' },
  },
  faq: {
    kicker: '§07 FAQ',
    title: 'Before you go out',
    // B6 (1. 10. 2026): two kinds of ad (energy, heal); the Trader's ad refresh is gone.
    q1: { q: 'Is it really free?', a: 'Yes. Every zone, stance and skill is free to play. Up to {n} optional ads a day give +{e} energy when you run low or a full heal, and nothing you can buy makes your survivor stronger.' },
    q2: { q: 'How much time does it take?', a: '5 to 15 minutes a day. Give orders in a minute, come back later to read the report. Expeditions run from 10 minutes to a few hours while you do something else.' },
    q3: { q: 'Do I need to download anything?', a: 'No. It runs in any modern browser, on your phone or your computer. Link your email to play on any device.' },
    q4: { q: 'What happens when I’m offline?', a: 'Your survivor follows the orders you gave: stance, retreat point, what to trade. If you’re online during an encounter, you can decide yourself: attack or escape by hand for a bonus.' },
    q5: { q: 'Can other players hurt me?', a: 'Yes, in most zones. PvP matches survivors of similar power, and {zones} are open PvP for anyone who wants the risk.' },
    q6: { q: 'What does beta mean here?', a: 'The world is live and progress is real, but numbers will change. If we ever have to reset, we’ll say so well in advance and give early players something to show for it.' },
    /** 4. 10. 2026 (package 3). */
    q7: { q: 'What happens when the season ends?', a: 'A season lasts {days} days. At its end everyone starts over on level 1 with a fresh camp, and the best in the Season Score get a Legendary outfit, titles and frames. Your Collection book, outfits, titles, achievements and Survivor Rank stay, and Legacy points buy perks that help in every season after.' },
  },
  final: {
    title: "The world ended. You didn't.",
  },
  footer: {
    beta: "Public beta. Expect bugs, balance changes and the occasional wipe. We'll tell you before it happens.",
    discord: 'Join the Discord',
    updates: 'Get updates',
    updatesBody: 'Leave your email and we will write when there is big news. Never sold.',
    email: 'Email address',
    emailPlaceholder: 'you@example.com',
    signUp: 'Sign up',
    badEmail: "That doesn't look like an email address.",
    filed: 'Filed',
    onList: "You're on the list.",
    copyright: '© 2026 AFTER DAY',
    privacy: 'Privacy',
    returning: 'Played before?',
    signIn: 'Log in',
  },
  signIn: {
    title: 'Log in',
    body: 'We email you a one-time link. No password. Your survivor follows you to any device.',
    email: 'Email address',
    send: 'Send login link',
    /** 29. 9. 2026: after a link that expired or was used. */
    sendNew: 'Send a new link',
    google: 'Continue with Google',
    or: 'or',
    failed: "Google login didn't work. Try again, or use email.",
    /** A survivor on this device that is not saved (a guest), before logging in to another account. */
    guestWarn: "This device has a survivor that isn't saved. If you log in to another account, you won't be able to get back to it.",
    saveFirst: 'Save it first',
    anyway: 'Log in anyway',
    sending: 'Sending…',
    stamp: 'Sent',
    sent: 'Check your inbox',
    sentBody: 'We sent a link to {email}. It works for 20 minutes.',
    devNote: 'Dev mode: no mail server is set up, so here is the link.',
    off: 'Email login is not available right now.',
    devOpen: 'Open login link',
    another: 'Use another email',
    expired: 'That login link expired or was already used.',
    newHere: 'New here? Play without an account',
  },
  // 13. 10. 2026 (owner): English names in the examples, as the NPC survivors in the game.
  mock: {
    survivor: 'Wes',
    rival: 'Grimsby',
    report: {
      number: 'Expedition #0417',
      returned: 'Returned 14:32',
      title: 'Your survivor is back',
      stamp: 'Returned',
      line: '{name} walked back in carrying more than he left with.',

      stance: 'Stance',
      log: 'Field log',
      window: '14:12 – 14:32',
      spotted: 'Spotted',
      rivalMeta: 'Lv 14 · Scavenger',
      followed: 'Followed him through 3 areas',
      undetected: 'Undetected',
      found: '{name} found',
      keyMoment: 'Key moment',
      hit: 'Ambush',
      hitBody: 'You stole 2 Medkits and escaped.',
      hitNote: '{name} never saw your face.',
      killed: 'Killed 2 Infected',
      killedNote: 'On the way out. Nothing personal.',
      aside: '{name} will remember this. Probably.',
      xp: 'XP',
      hpLost: 'HP lost',
      items: 'Items',
    },
    map: {
      title: 'World map',
      who: '{name} · Lv {n}',
      locked: 'Lv {n}',
      openPvp: 'Open PvP',
      lv: 'Lv {a}–{b}',
      lvMin: 'Lv {n}+',
      camp: 'Camp',
      above: 'Above your level ({n})',
    },
    cards: {
      ambush: {
        meta: 'DAY 212 · 14:23 · HOSPITAL',
        headline: 'Tamsin was ambushed by Grimsby',
        a: 'LV 12 · SCAVENGER',
        b: 'LV 14 · SCAVENGER',
        result: 'TAMSIN ESCAPED',
        loot: 'Loot lost: 2 Medkits',
        cta: 'Revenge available — play AFTER DAY',
      },
      escape: {
        meta: 'DAY 212 · 18:05 · FACTORY',
        headline: 'Escaped with 4 HP',
        a: 'LV 12 · HUNTER',
        bName: 'Raiders',
        b: '×4 · ARMED',
        result: 'FINN MADE IT HOME',
        statSub: 'HP left',
        cta: 'Could you do better? Play AFTER DAY',
      },
      legendary: {
        meta: 'DAY 212 · 09:41 · MILITARY BASE',
        headline: 'Grimsby found a legendary weapon',
        a: 'LV 14 · SOLDIER',
        cta: 'Go find yours — play AFTER DAY',
      },
    },
  },
} as const;
